import type { ComponentRef, Ref } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { cn } from "../../utils";
import { ChevronDown, Check } from "../../atoms/Icon";
import type {
  SelectTriggerProps,
  SelectContentProps,
  SelectItemProps,
  SelectLabelProps,
  SelectSeparatorProps,
} from "./types";

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

interface SelectTriggerPropsWithRef extends SelectTriggerProps {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Trigger>>;
}

function SelectTrigger({ className, children, ref, ...props }: SelectTriggerPropsWithRef) {
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-lg border border-border-default bg-white px-3 py-2 text-sm text-primary placeholder:text-placeholder focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-border-focus disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        "dark:border-dark-border-default dark:bg-dark-bg-secondary dark:text-dark-text-primary dark:placeholder:text-dark-text-placeholder dark:focus:ring-primary-500/20 dark:focus:border-dark-border-focus",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown size="sm" className="text-placeholder dark:text-dark-text-placeholder" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

interface SelectContentPropsWithRef extends SelectContentProps {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Content>>;
}

function SelectContent({
  className,
  children,
  position = "popper",
  ref,
  ...props
}: SelectContentPropsWithRef) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-lg border border-border-default bg-white shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          "dark:border-dark-border-default dark:bg-dark-bg-card dark:text-dark-text-primary",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

interface SelectLabelPropsWithRef extends SelectLabelProps {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Label>>;
}

function SelectLabel({ className, ref, ...props }: SelectLabelPropsWithRef) {
  return (
    <SelectPrimitive.Label
      ref={ref}
      className={cn(
        "py-1.5 pl-8 pr-2 text-sm font-semibold text-primary dark:text-dark-text-primary",
        className
      )}
      {...props}
    />
  );
}

interface SelectItemPropsWithRef extends SelectItemProps {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Item>>;
}

function SelectItem({ className, children, ref, ...props }: SelectItemPropsWithRef) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-md py-2 pl-8 pr-2 text-sm text-primary outline-none focus:bg-neutral-100 focus:text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        // 기본 상태 다크 글자색이 없으면 text-primary(어두운 글자)가 그대로 남아
        // dark:bg-dark-bg-card 위에서 묻힌다(포커스된 항목만 보이는 증상).
        "dark:text-dark-text-primary dark:focus:bg-dark-bg-hover dark:focus:text-dark-text-primary",
        className
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check size="sm" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

interface SelectSeparatorPropsWithRef extends SelectSeparatorProps {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Separator>>;
}

function SelectSeparator({ className, ref, ...props }: SelectSeparatorPropsWithRef) {
  return (
    <SelectPrimitive.Separator
      ref={ref}
      className={cn("-mx-1 my-1 h-px bg-border-default dark:bg-dark-border-default", className)}
      {...props}
    />
  );
}

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
};
